'use client';

import { useTranslations } from 'next-intl';
import React, { useState, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import rehypeRaw from 'rehype-raw';

import VideoLink from '@/components/video/VideoLink';

import { CinemaBlogDto } from '@cineamo/api/models/cinema-blog/CinemaBlogDto.types';
import { ImageDto } from '@cineamo/api/models/image/ImageDto.types';

import { isYoutubeVideoUrl } from '@/constants/youtube-constants';

import rehypeAllowlist from '@/helpers/rehype-allowlist';
import { getBlogPostUrl } from '@cineamo/ui/src/helpers/url-helper';

import ImageGallery from '@cineamo/ui/src/image/ImageGallery';
import ImageWithTransition from '@cineamo/ui/src/image/ImageWithTransition';

const CinemaBlogItem: React.FC<{
    cinemaBlog: CinemaBlogDto;
    cinemaBlogImages: ImageDto[];
}> = ({ cinemaBlog, cinemaBlogImages }) => {
    const t = useTranslations();
    const [imageGalleryOpenWithData, setImageGalleryOpenWithData] = useState<{ index: number; images: string[] }>();
    const cinemaBlogId = cinemaBlog.id;
    const hasImages = cinemaBlogImages.map((image) => image.imageUrl).length > 0;
    const blogId = useMemo(() => getBlogPostUrl(cinemaBlog), [cinemaBlog]);
    return (
        <div
            className="flex flex-col space-y-16 px-[9px] lg:px-0"
            id={blogId}>
            {imageGalleryOpenWithData && (
                <ImageGallery
                    images={imageGalleryOpenWithData.images}
                    open={true}
                    onClose={() => setImageGalleryOpenWithData(undefined)}
                    currentIndex={imageGalleryOpenWithData.index}
                />
            )}
            <div className="flex flex-col max-w-[600px] space-y-16">
                <h2 className="text-headline-24">{cinemaBlog.title}</h2>

                <div>
                    <ReactMarkdown
                        rehypePlugins={[rehypeRaw, rehypeAllowlist]}
                        components={MDComponents()}>
                        {cinemaBlog.content}
                    </ReactMarkdown>
                </div>
            </div>

            {hasImages && (
                <div className="flex flex-wrap gap-[6px] items-center">
                    {cinemaBlogImages.map((image, index) => (
                        <button
                            key={`BlogItemImage#${cinemaBlogId}-${index}`}
                            type="button"
                            aria-label={t('common.enlarge-image')}
                            className="max-w-full clickable-element"
                            onClick={() =>
                                setImageGalleryOpenWithData({
                                    images: cinemaBlogImages.map((image) => image.imageUrl ?? ''),
                                    index: index
                                })
                            }>
                            <ImageWithTransition
                                className="w-auto max-w-full h-min max-h-[200px] object-contain overflow-clip rounded-8"
                                src={image.imageUrl ?? ''}
                                width={500}
                                height={200}
                                alt={image.imageDescription || ''}
                            />
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default CinemaBlogItem;

function MDComponents(): Partial<Components> {
    return {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        h1: ({ node, ...props }) => (
            <h1
                {...props}
                className="text-headline-20 whitespace-pre-wrap"
            />
        ),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        h2: ({ node, ...props }) => (
            <h2
                {...props}
                className="text-headline-18 whitespace-pre-wrap"
            />
        ),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        h3: ({ node, ...props }) => (
            <h3
                {...props}
                className="text-body-bold-16 whitespace-pre-wrap"
            />
        ),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        p: ({ node, ...props }) => (
            <p
                {...props}
                className="text-body-14 whitespace-pre-wrap"
            />
        ),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        a: ({ node, href, ...props }) => {
            // A YouTube url is treated as a video: the link opens the player modal instead of navigating
            if (isYoutubeVideoUrl(href)) {
                return (
                    <VideoLink
                        url={href}
                        className="text-bold whitespace-pre-wrap text-[var(--secondary-color)]"
                        trackingEvent="News Video Played">
                        {props.children}
                    </VideoLink>
                );
            }
            return (
                <a
                    {...props}
                    href={href}
                    className="text-bold whitespace-pre-wrap text-[var(--secondary-color)]"
                    target="_blank"
                />
            );
        },
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        strong: ({ node, ...props }) => (
            <strong
                {...props}
                className="font-montserrat-bold whitespace-pre-wrap"
            />
        ),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        blockquote: ({ node, ...props }) => (
            <blockquote
                {...props}
                className="pl-[16px] italic text-body-14"
            />
        ),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ul: ({ node, ...props }) => (
            <ul
                {...props}
                className="list-disc pl-[12px]"
            />
        ),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ol: ({ node, ...props }) => (
            <ol
                {...props}
                className="list-decimal pl-[12px]"
            />
        ),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        li: ({ node, ...props }) => (
            <li
                {...props}
                className="ml-[16px] text-body-14 whitespace-pre-wrap"
            />
        )
    };
}
